import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {Services} from "../../Services";
import {Resources} from "../../../resources/Resources";
import {ErrorCode} from "../../../network/status/error/ErrorCode";
import {ICommunityIndexationOverviewIncoming} from "../../../models/community/seo/indexation/ICommunityIndexationOverviewIncoming";
import {ICommunityIndexationPackIncoming} from "../../../models/community/seo/indexation/ICommunityIndexationPackIncoming";
import {ICommunityIndexationPagesIncoming} from "../../../models/community/seo/indexation/ICommunityIndexationPagesIncoming";
import {ICommunityIndexationDetailIncoming} from "../../../models/community/seo/indexation/ICommunityIndexationDetailIncoming";
import {ICommunityIndexationPagesSearchOutgoing} from "../../../models/community/seo/indexation/ICommunityIndexationPagesSearchOutgoing";
import {ICommunityIndexationFiltersValues} from "../../../models/community/seo/indexation/ICommunityIndexationFiltersValues";
import {ICommunityIndexationRequestAllPreviewIncoming} from "../../../models/community/seo/indexation/ICommunityIndexationRequestAllPreviewIncoming";
import {ICommunityIndexationConfigOutgoing} from "../../../models/community/seo/indexation/ICommunityIndexationConfigOutgoing";
import {ICommunityIndexationConfigModel} from "../../../models/community/seo/indexation/ICommunityIndexationConfigModel";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {ICommunityIndexationFilter} from "../../../models/community/seo/indexation/ICommunityIndexationFilter";

export class CommunityIndexationService {

    public static changeEvents: (() => void)[] = [];

    public static dispose(): void {
        this.changeEvents = [];
    }

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_INDEXATION_CHANGED, () => {
            this.changeEvents.forEach(value => value());
        });
    }

    public static onChange(component: INetworkComponent, func: () => void) {
        this.changeEvents.push(func);
        component.onRemove(() => arrayRemove(this.changeEvents, func));
    }

    public static async getOverview(communityId: string, component?: INetworkComponent): Promise<ICommunityIndexationOverviewIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/indexation/overview/${communityId}`, component);
        return request.data;
    }

    // prix d'un montant libre de crédits, calculé par le back (source unique) — le front ne fait qu'afficher
    public static async getPriceForCredits(communityId: string, credits: number, component?: INetworkComponent): Promise<ICommunityIndexationPackIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/indexation/price/${communityId}?credits=${credits}`, component);
        return request.data;
    }

    public static async discoverSitemaps(communityId: string, component?: INetworkComponent): Promise<string[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/indexation/sitemaps/discover/${communityId}`, component);
        return request.data;
    }

    public static async getPages(search: ICommunityIndexationPagesSearchOutgoing, component?: INetworkComponent): Promise<ICommunityIndexationPagesIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/pages`, search, component);
        return request.data;
    }

    public static async getDetail(communityId: string, id: string, component?: INetworkComponent, live: boolean = false): Promise<ICommunityIndexationDetailIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/indexation/detail/${communityId}?id=${encodeURIComponent(id)}&live=${live}`, component);
        return request.data;
    }

    public static async request(communityId: string, ids: string[], component?: INetworkComponent): Promise<number> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/request`, {
            communityId: communityId,
            ids: ids
        }, component);
        return request.data;
    }

    // "tout demander" : mêmes filtres que le tableau ; preview (combien de pages) puis exécution plafonnée au solde
    public static async requestAllPreview(communityId: string, filters: ICommunityIndexationFiltersValues, component?: INetworkComponent): Promise<ICommunityIndexationRequestAllPreviewIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/request/all/preview`, {communityId, ...filters}, component);
        return request.data;
    }

    public static async requestAll(communityId: string, filters: ICommunityIndexationFiltersValues, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/request/all`, {communityId, ...filters}, component);
        return request.status == HttpStatus.OK;
    }

    public static async importUrls(communityId: string, urls: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/import`, {
            communityId: communityId,
            urls: urls
        }, component);
        return request.status == HttpStatus.OK;
    }

    public static async config(config: ICommunityIndexationConfigOutgoing, component?: INetworkComponent): Promise<ICommunityIndexationConfigModel> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/config`, config, component);
        return request.data;
    }

    public static async activate(communityId: string, component?: INetworkComponent): Promise<ICommunityIndexationConfigModel> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/activate/${communityId}`, {}, component);
        return request.data;
    }

    public static async reset(communityId: string, filters: ICommunityIndexationFilter[], component?: INetworkComponent): Promise<boolean> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/reset`, {
            communityId: communityId,
            filters: filters
        }, component);
        return request.status == HttpStatus.OK;
    }

    public static async deletePage(communityId: string, id: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/indexation/delete`, {
            communityId: communityId,
            id: id
        }, component);
        return request.status == HttpStatus.OK;
    }

    public static async convert(communityId: string, credits: number, component?: INetworkComponent): Promise<boolean> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.INSUFFICIENT_CREDIT, message: Resources.t("words.indexationCreditInsufficient")}
        ]);

        let request = await Network.postJson(ProductType.COMMUNITY, `/community/indexation/convert`, {
            communityId: communityId,
            credits: credits
        }, component);
        return request.status == HttpStatus.OK;
    }
}
